import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import Placeholder from '@tiptap/extension-placeholder';
import SuggestionMark from './extensions/SuggestionMark';

const API = '/api/work/documents';

export default function CollectiveDocEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, isAuthenticated } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [activeSuggestion, setActiveSuggestion] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [isGuest, setIsGuest] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [voteCounts, setVoteCounts] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const providerRef = useRef(null);
  const editorRef = useRef(null);

  // Guest auth via query token or localStorage
  useEffect(() => {
    if (!isAuthenticated && token) {
      localStorage.setItem(`guest_token_${id}`, token);
      setIsGuest(true);
    } else if (!isAuthenticated) {
      const saved = localStorage.getItem(`guest_token_${id}`);
      if (saved) setIsGuest(true);
      else setShowGuestModal(true);
    }
  }, [isAuthenticated, token, id]);

  // Fetch document metadata
  useEffect(() => {
    async function fetchDoc() {
      try {
        const headers = {};
        const guestToken = localStorage.getItem(`guest_token_${id}`);
        if (guestToken) headers['X-Guest-Token'] = guestToken;

        const res = await fetch(`${API}/${id}`, { credentials: 'include', headers });
        if (!res.ok) throw new Error('Failed to load document');
        const data = await res.json();
        setDoc(data.document);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    if (id) fetchDoc();
  }, [id]);

  // Fetch suggestions
  useEffect(() => {
    async function fetchSuggestions() {
      const res = await fetch(`${API}/${id}/suggestions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    }
    if (id && !loading) fetchSuggestions();
  }, [id, loading]);

  // Fetch comments
  useEffect(() => {
    async function fetchComments() {
      const res = await fetch(`${API}/${id}/comments`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    }
    if (id && !loading) fetchComments();
  }, [id, loading]);

  // Fetch votes
  useEffect(() => {
    async function fetchVotes() {
      const res = await fetch(`${API}/${id}/votes`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVoteCounts(data.voteCounts || {});
        setUserVotes(data.userVotes || {});
      }
    }
    if (id && !loading) fetchVotes();
  }, [id, loading]);

  // Setup Hocuspocus provider for collaborative editing
  useEffect(() => {
    if (!id) return;

    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/collab`;
    const provider = new HocuspocusProvider({
      url: wsUrl,
      name: `collective-doc-${id}`,
      token: user?.id || localStorage.getItem(`guest_token_${id}`) || 'guest',
    });
    providerRef.current = provider;

    return () => provider.destroy();
  }, [id, user?.id]);

  // TipTap editor with collaboration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      Collaboration.configure({ document: providerRef.current?.document }),
      CollaborationCursor.configure({
        provider: providerRef.current,
        user: { name: user?.displayName || guestName || 'Guest', color: getUserColor() },
      }),
      Placeholder.configure({ placeholder: 'Start typing...' }),
      SuggestionMark,
    ],
    editable: true,
  });

  editorRef.current = editor;

  function getUserColor() {
    const colors = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444', '#ec4899'];
    const hash = (user?.id || guestEmail || '').split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    return colors[Math.abs(hash) % colors.length];
  }

  // BUG FIX: getInitials now properly uses the user variable from the component scope
  const getInitials = (name) => {
    if (!name) return user?.email?.[0]?.toUpperCase() || guestEmail?.[0]?.toUpperCase() || '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  async function handleGuestSubmit(e) {
    e.preventDefault();
    if (!guestName || !guestEmail) return;

    const res = await fetch(`${API}/${id}/guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: guestName, email: guestEmail }),
    });

    if (res.ok) {
      const data = await res.json();
      localStorage.setItem(`guest_token_${id}`, data.token);
      setIsGuest(true);
      setShowGuestModal(false);
    }
  }

  async function addSuggestion() {
    if (!editor || !user) return;
    const { from, to } = editor.state.selection;
    if (from === to) return alert('Please select some text first');

    const selectedText = editor.state.doc.textBetween(from, to);
    const comment = prompt('Enter your suggestion:');
    if (!comment) return;

    const res = await fetch(`${API}/${id}/suggestions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        from,
        to,
        selectedText,
        comment,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      setSuggestions([...suggestions, data.suggestion]);
      editor.chain().focus().setMark('suggestion', { suggestionId: data.suggestion.id }).run();
    }
  }

  async function voteOnSuggestion(suggestionId, voteType) {
    const res = await fetch(`${API}/${id}/suggestions/${suggestionId}/vote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ vote: voteType }),
    });

    if (res.ok) {
      const data = await res.json();
      setVoteCounts({ ...voteCounts, [suggestionId]: data.voteCount });
      setUserVotes({ ...userVotes, [suggestionId]: voteType });
    }
  }

  async function resolveSuggestion(suggestionId, accept) {
    const res = await fetch(`${API}/${id}/suggestions/${suggestionId}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ accept }),
    });

    if (res.ok) {
      setSuggestions(suggestions.map(s => s.id === suggestionId ? { ...s, status: accept ? 'accepted' : 'rejected' } : s));
      if (accept) {
        // Apply the suggested change to the document
        const suggestion = suggestions.find(s => s.id === suggestionId);
        if (suggestion && editor) {
          editor.chain().focus().deleteRange({ from: suggestion.from, to: suggestion.to }).insertContent(suggestion.suggestedText || suggestion.selectedText).run();
        }
      }
    }
  }

  async function addComment() {
    const text = prompt('Enter your comment:');
    if (!text) return;

    const res = await fetch(`${API}/${id}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ text }),
    });

    if (res.ok) {
      const data = await res.json();
      setComments([...comments, data.comment]);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <a href="/work" className="text-blue-600 hover:underline">Back to Work</a>
        </div>
      </div>
    );
  }

  if (showGuestModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <h2 className="text-xl font-semibold mb-4">Join Collaborative Document</h2>
          <p className="text-gray-600 mb-6">Enter your details to contribute to this document.</p>
          <form onSubmit={handleGuestSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="your@email.com"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Join Document
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/work" className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </a>
            <div>
              <h1 className="font-semibold text-gray-900">{doc?.title || 'Untitled'}</h1>
              <p className="text-xs text-gray-500">Collective Document</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {showSidebar ? 'Hide' : 'Show'} Sidebar
            </button>
            {user && (
              <button
                onClick={addSuggestion}
                className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg transition-colors"
              >
                Add Suggestion
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold">
                {getInitials(user?.displayName || guestName)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex">
        {/* Editor */}
        <div className={`flex-1 p-8 transition-all ${showSidebar ? 'pr-80' : ''}`}>
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 min-h-[800px] p-8">
            <EditorContent editor={editor} className="prose prose-gray max-w-none" />
          </div>
        </div>

        {/* Sidebar */}
        {showSidebar && (
          <div className="w-80 bg-white border-l border-gray-200 fixed right-0 top-14 bottom-0 overflow-y-auto">
            {/* Suggestions */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Suggestions</h3>
                <span className="text-xs text-gray-500">{suggestions.length}</span>
              </div>
              <div className="space-y-3">
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      activeSuggestion === suggestion.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveSuggestion(suggestion.id)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs flex-shrink-0">
                        {getInitials(suggestion.author?.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">{suggestion.selectedText}</p>
                        <p className="text-xs text-gray-500 mt-1">{suggestion.comment}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); voteOnSuggestion(suggestion.id, 'up'); }}
                            className={`text-xs px-2 py-1 rounded ${userVotes[suggestion.id] === 'up' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                          >
                            ▲ {voteCounts[suggestion.id]?.up || 0}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); voteOnSuggestion(suggestion.id, 'down'); }}
                            className={`text-xs px-2 py-1 rounded ${userVotes[suggestion.id] === 'down' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}
                          >
                            ▼ {voteCounts[suggestion.id]?.down || 0}
                          </button>
                        </div>
                        {doc?.isOwner && (
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); resolveSuggestion(suggestion.id, true); }}
                              className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                            >
                              Accept
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); resolveSuggestion(suggestion.id, false); }}
                              className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {suggestions.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No suggestions yet</p>
                )}
              </div>
            </div>

            {/* Comments */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Comments</h3>
                <button
                  onClick={addComment}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Add
                </button>
              </div>
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs flex-shrink-0">
                      {getInitials(comment.author?.name)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{comment.text}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
